if (!customElements.get('wlsn-product-facet')) {
  class WLSNProductFacet extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;

      this.classes = {
        loading: 'loading',
        results: 'wlsn-product-facet',
        grid: 'wlsn-product-grid__grid',
        infinitePagination: 'wlsn-infinite-pagination',
      };

      this.paginationTypeConstants = {
        INFINITE: 'infinite',
        BUTTONS: 'buttons',
        NONE: 'none',
      };

      this.paginationType = this.getAttribute('data-pagination-type') || this.paginationTypeConstants.BUTTONS;
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      if (this.paginationType === this.paginationTypeConstants.INFINITE) {
        this.addEventListener('wlsn:infinite-pagination:load-more', this._onInfiniteLoadMore.bind(this));
      } else if (this.paginationType === this.paginationTypeConstants.BUTTONS) {
        this.addEventListener('wlsn:pagination:page-changed', this._onPaginationChange.bind(this));
      }
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      if (this.paginationType === this.paginationTypeConstants.INFINITE) {
        this.removeEventListener('wlsn:infinite-pagination:load-more', this._onInfiniteLoadMore);
      } else if (this.paginationType === this.paginationTypeConstants.BUTTONS) {
        this.removeEventListener('wlsn:pagination:page-changed', this._onPaginationChange);
      }
    }

    /**
     * Handle pagination change event
     * @param {Event} event - The pagination change event
     * @returns {Promise<void>}
     */
    async _onPaginationChange(event) {
      const url = new URL(event.detail.url);

      this._abort();
      this._setLoading();

      try {
        this.abortController = new AbortController();
        const res = await fetch(url.toString(), { signal: this.abortController.signal });
        const html = await res.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newFacet = doc.querySelector(`${this.classes.results}`);

        if (newFacet) {
          this.innerHTML = newFacet.innerHTML;
          history.replaceState({}, '', url);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(error);
        }
      } finally {
        this._removeLoading();
      }
    }

    /**
     * Handle infinite scroll load more event
     * @param {Event} event - The load more event
     * @returns {Promise<void>}
     */
    async _onInfiniteLoadMore(event) {
      const url = new URL(event.detail.url);

      this._abort();
      this._setLoading();

      try {
        this.abortController = new AbortController();
        const res = await fetch(url.toString(), { signal: this.abortController.signal });
        const html = await res.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Get the new products from the fetched page
        const newFacet = doc.querySelector(`${this.classes.results}`);
        const newGrid = newFacet?.querySelector(`.${this.classes.grid}`);
        const newPagination = newFacet?.querySelector(`.${this.classes.infinitePagination}`);

        // Get current pagination component
        const currentPagination = this.querySelector(`.${this.classes.infinitePagination}`);

        if (newGrid) {
          // Get current grid and append new products
          const currentGrid = this.querySelector(`.${this.classes.grid}`);
          if (currentGrid) {
            // Get all product cards from new grid (excluding review cards)
            const newProducts = Array.from(newGrid.children);
            newProducts.forEach((product) => {
              currentGrid.appendChild(product);
            });
          }

          // Update infinite pagination component with new data
          if (currentPagination) {
            if (newPagination) {
              // New pagination exists - update with new data
              const newPage = parseInt(newPagination.dataset.currentPage) || 1;
              const newTotalPages = parseInt(newPagination.dataset.totalPages) || 1;
              const newNextUrl = newPagination.dataset.nextUrl || '';

              currentPagination.updatePagination(newPage, newTotalPages, newNextUrl);
            } else {
              // No new pagination found - we've reached the end
              currentPagination.markComplete();
            }
          }

          // Note: URL is NOT updated for infinite scroll - user stays on current page
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[WLSNProductFacet] Infinite load error:', error);
        }
        // Remove loading state on error
        const currentPagination = this.querySelector(`.${this.classes.infinitePagination}`);
        if (currentPagination) {
          currentPagination.removeLoadingState();
          currentPagination.isLoading = false;
        }
      } finally {
        this._removeLoading();
      }
    }

    /**
     * Abort current fetch request
     * @returns {void}
     */
    _abort() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    }

    /**
     * Set loading state
     * @returns {void}
     */
    _setLoading() {
      this.classList.add(this.classes.loading);
    }

    /**
     * Remove loading state
     * @returns {void}
     */
    _removeLoading() {
      this.classList.remove(this.classes.loading);
    }
  }

  customElements.define('wlsn-product-facet', WLSNProductFacet);
}
